import {
  FieldType,
  CMS_FIELD_PLUGIN_ID,
  CATEGORY_FIELD_PLUGIN_ID,
  PRIMARY_CATEGORY_FIELD_PLUGIN_ID,
  CATEGORY_CONSUMER_FIELD_PLUGIN_ID,
} from '@wix/wix-data-schema-types'
import {
  string,
  optional,
  boolean,
  objectProperties,
  list,
  object,
} from './utils/validate'
import { typeMetadataSchemas, withTypeMetadata } from './typeMetadataSchemas'

const genericFieldTypeConfigSchema = {
  type: optional(string),
  displayName: optional(string),
  pluginId: optional(string),
  plugin: optional(string),
  queryOperators: optional(list(string)),
  systemField: optional(boolean),
  sortable: optional(boolean),
  isDeleted: optional(boolean),
  pii: optional(boolean),
  readOnly: optional(boolean),
  description: optional(string),
  required: optional(boolean),
  plugins: optional(
    objectProperties({
      [CMS_FIELD_PLUGIN_ID]: optional(
        objectProperties({
          internal: optional(boolean),
        }),
      ),
    }),
  ),
}

const pagelinkSchema = {
  ...genericFieldTypeConfigSchema,
  linkedRouterPage: string,
  pageLinkType: optional(string),
  calculator: objectProperties({
    id: optional(string),
    config: objectProperties({
      pattern: string,
      lowercase: optional(boolean),
    }),
  }),
}

const referenceTypeConfigSchema = {
  ...genericFieldTypeConfigSchema,
  referencedCollection: string,
  plugins: optional(
    objectProperties({
      [CMS_FIELD_PLUGIN_ID]: optional(
        objectProperties({
          internal: optional(boolean),
        }),
      ),
      [PRIMARY_CATEGORY_FIELD_PLUGIN_ID]: optional(objectProperties({})),
    }),
  ),
}

const multiReferenceTypeConfigSchema = {
  ...referenceTypeConfigSchema,
  referencingFieldKey: string,
  referencingDisplayName: string,
  plugins: optional(
    objectProperties({
      [CMS_FIELD_PLUGIN_ID]: optional(
        objectProperties({
          internal: optional(boolean),
        }),
      ),
      [CATEGORY_FIELD_PLUGIN_ID]: optional(
        objectProperties({
          isPrimary: optional(boolean),
        }),
      ),
      [CATEGORY_CONSUMER_FIELD_PLUGIN_ID]: optional(objectProperties({})),
    }),
  ),
}

const arrayTypeConfigSchema = {
  ...genericFieldTypeConfigSchema,
  elementType: optional(
    objectProperties({
      type: optional(string),
    }),
  ),
  ...typeMetadataSchemas[FieldType.array],
}

const objectTypeConfigSchema = {
  ...genericFieldTypeConfigSchema,
  fields: optional(object),
  ...typeMetadataSchemas[FieldType.object],
}

const slugSchema = {
  ...genericFieldTypeConfigSchema,
  pattern: string,
  ...typeMetadataSchemas[FieldType.slug],
}

const composeFieldConfig = (fieldType: keyof typeof typeMetadataSchemas) =>
  withTypeMetadata(genericFieldTypeConfigSchema, fieldType)

export {
  genericFieldTypeConfigSchema,
  pagelinkSchema,
  referenceTypeConfigSchema,
  multiReferenceTypeConfigSchema,
  arrayTypeConfigSchema,
  objectTypeConfigSchema,
}

const fieldConfigsFromTypeMetadata = Object.fromEntries(
  (
    Object.keys(typeMetadataSchemas) as Array<keyof typeof typeMetadataSchemas>
  ).map(fieldType => [fieldType, composeFieldConfig(fieldType)]),
)

export default {
  default: genericFieldTypeConfigSchema,
  ...fieldConfigsFromTypeMetadata,
  [FieldType.pageLink]: pagelinkSchema,
  [FieldType.reference]: referenceTypeConfigSchema,
  [FieldType.multiReference]: multiReferenceTypeConfigSchema,
  [FieldType.array]: arrayTypeConfigSchema,
  [FieldType.object]: objectTypeConfigSchema,
  [FieldType.slug]: slugSchema,
}
